<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { GetEditorialSearchResultsProjectResponse, GetEditorialSearchResultsResponse } from '@/api';
import SectionHeader from '@/components/SectionHeader.vue';
import { getProjectBedroomPrices, getStatistics, selectBedroomColumns, type EditorialFaqItem } from '@/views/editorial/useEditorialStatisticsSection.ts';
import { getMinPrice } from '@/views/search/index.ts';
import { Constants } from '@/utilities/constants';

const props = defineProps<{
  response: GetEditorialSearchResultsResponse;
  projects: GetEditorialSearchResultsProjectResponse[];
  title: string;
  locationPhrase: string;
  faqItems: EditorialFaqItem[];
  bedroomColumnsMinimumFill: number;
}>();

const { t, locale } = useI18n();

const statistics = computed(() => getStatistics(props.response));

// Le paragraphe dense est assemblé de phrases autonomes : chacune reste vraie et citable hors contexte
const summarySentence = computed(() =>
  t('views.editorial.statistics.summary', {
    title: props.title.split(':')[0]?.trim() ?? props.title,
    projects: t('views.editorial.statistics.summary-projects', { count: props.response.count }, props.response.count),
    builders: t('views.editorial.statistics.summary-builders', { count: statistics.value?.builderCount ?? 0 }, statistics.value?.builderCount ?? 0),
  })
);

const typesSentence = computed(() => {
  if (!statistics.value) return '';

  const types = statistics.value.categories.map((category) => t('views.editorial.statistics.summary-type-entry', { name: category.categoryName, price: category.minimumSalePriceAmount.formatCurrency(locale.value) })).join(', ');

  return t('views.editorial.statistics.summary-types', { types });
});

const bedroomsSentence = computed(() => {
  if (!statistics.value || statistics.value.bedroomMinimumPrices.length === 0) return '';

  const bedrooms = statistics.value.bedroomMinimumPrices
    .map((entry) =>
      entry.bedrooms === 0
        ? t('views.editorial.statistics.summary-studio-entry', { price: entry.minimumSalePriceAmount.formatCurrency(locale.value) })
        : t('views.editorial.statistics.summary-bedroom-entry', { n: entry.bedrooms, price: entry.minimumSalePriceAmount.formatCurrency(locale.value) }, entry.bedrooms)
    )
    .join(', ');

  return t('views.editorial.statistics.summary-bedrooms', { bedrooms });
});

const amenitiesSentence = computed(() => {
  if (!statistics.value || statistics.value.amenities.length === 0) return '';

  const amenities = statistics.value.amenities.map((entry) => t('views.editorial.statistics.summary-amenity-entry', { name: entry.name, count: entry.projectCount }, entry.projectCount)).join(', ');

  return t('views.editorial.statistics.summary-amenities', { amenities });
});

const updatedSentence = computed(() => t('views.editorial.statistics.summary-updated', { date: new Intl.DateTimeFormat(locale.value, { month: 'long', year: 'numeric' }).format(new Date()) }));

const formatRange = (minimum: number, maximum: number, format: (value: number) => string) =>
  minimum === maximum ? format(minimum) : t('views.editorial.statistics.types-table.range', { minimum: format(minimum), maximum: format(maximum) });

const categoryRows = computed(() => {
  if (!statistics.value) return [];

  return statistics.value.categories.map((category) => ({
    id: category.categoryId,
    name: category.categoryName,
    roomsApplicable: category.roomsApplicable,
    projectCount: category.projectCount,
    bedrooms: category.minimumBedrooms !== null && category.maximumBedrooms !== null ? formatRange(category.minimumBedrooms, category.maximumBedrooms, (value) => value.formatNumber(locale.value)) : null,
    priceRange:
      category.minimumSalePriceAmount === category.maximumSalePriceAmount
        ? category.minimumSalePriceAmount.formatCurrency(locale.value)
        : t('views.editorial.statistics.types-table.range', { minimum: category.minimumSalePriceAmount.formatCurrency(locale.value), maximum: category.maximumSalePriceAmount.formatCurrency(locale.value) }),
    floorArea: category.medianFloorArea !== null ? t('views.editorial.statistics.types-table.floor-area-value', { median: category.medianFloorArea.formatNumber(locale.value) }) : null,
    floorAreaRange:
      category.minimumFloorArea !== null && category.maximumFloorArea !== null && category.minimumFloorArea !== category.maximumFloorArea
        ? t('views.editorial.statistics.types-table.range', { minimum: category.minimumFloorArea.formatNumber(locale.value), maximum: category.maximumFloorArea.formatNumber(locale.value) })
        : null,
    pricePerSquareFoot: category.medianSalePricePerSquareFoot !== null ? Math.round(category.medianSalePricePerSquareFoot).formatCurrency(locale.value) : null,
  }));
});

// La superficie et le prix au pied carré d'un terrain mesurent le terrain, pas un espace habitable :
// les deux familles vivent dans deux tableaux distincts pour que les en-têtes restent sans ambiguïté
const dwellingRows = computed(() => categoryRows.value.filter((row) => row.roomsApplicable));
const lotRows = computed(() => categoryRows.value.filter((row) => !row.roomsApplicable));

const bedroomColumns = computed(() => selectBedroomColumns(props.projects, Constants.PriceTypeId.ForSale, props.bedroomColumnsMinimumFill));

// Zéro chambre porte le nom du produit plutôt qu'un décompte, ici comme sur la fiche de projet
const bedroomColumnLabel = (bedrooms: number) => (bedrooms === 0 ? t('views.project.characteristics.loft-studio') : t('views.editorial.statistics.projects-table.bedroom-column', { n: bedrooms }, bedrooms));

const projectRows = computed(() =>
  props.projects.map((project) => {
    const categoryCounts = new Map<number, { name: string; roomsApplicable: boolean; count: number }>();
    for (const model of project.models) {
      const entry = categoryCounts.get(model.category.id) ?? { name: model.category.name, roomsApplicable: model.category.roomsApplicable, count: 0 };
      entry.count += 1;
      categoryCounts.set(model.category.id, entry);
    }
    const dominant = [...categoryCounts.values()].sort((a, b) => b.count - a.count)[0] ?? null;
    const dominantCategory = dominant?.name ?? null;

    // Un même projet peut offrir des habitations et des terrains : la ligne ne décrit que la famille
    // annoncée dans sa colonne Type, sinon la superficie d'un terrain se mêlerait à un espace habitable
    const rowModels = dominant ? project.models.filter((model) => model.category.roomsApplicable === dominant.roomsApplicable) : project.models;

    const bedrooms = rowModels.flatMap((model) => [model.bedrooms, model.bedroomsMax]).filter((value): value is number => value !== null && value > 0);
    // Les salles d'eau comptent pour une demie, comme sur la fiche de projet
    const bathrooms = rowModels.map((model) => model.bathrooms + model.halfBathrooms * 0.5).filter((value) => value > 0);
    const floorAreas = rowModels.map((model) => model.floorArea).filter((value) => value > 0);

    const minPrice = getMinPrice({ ...project, models: rowModels }, Constants.PriceTypeId.ForSale);

    return {
      id: project.id,
      name: project.name,
      route: { name: 'project', params: { slug: project.name.toSlug(), projectId: project.id } },
      category: dominantCategory,
      bedrooms: bedrooms.length > 0 ? formatRange(Math.min(...bedrooms), Math.max(...bedrooms), (value) => value.formatNumber(locale.value)) : null,
      bathrooms: bathrooms.length > 0 ? formatRange(Math.min(...bathrooms), Math.max(...bathrooms), (value) => new Intl.NumberFormat(locale.value, { maximumFractionDigits: 1 }).format(value)) : null,
      floorArea: floorAreas.length > 0 ? t('views.editorial.statistics.types-table.floor-area-value', { median: formatRange(Math.min(...floorAreas), Math.max(...floorAreas), (value) => value.formatNumber(locale.value)) }) : null,
      price: minPrice ? minPrice.amount.formatCurrency(locale.value) : null,
      priceAmount: minPrice?.amount ?? null,
      ...(() => {
        const prices = getProjectBedroomPrices(project, Constants.PriceTypeId.ForSale);
        const shown = bedroomColumns.value.map((column) => {
          const amount = prices.get(column.bedrooms);

          return { bedrooms: column.bedrooms, amount: amount ?? null, price: amount === undefined ? null : amount.formatCurrency(locale.value) };
        });
        const shownAmounts = shown.map((cell) => cell.amount).filter((amount): amount is number => amount !== null);

        return { bedroomPrices: shown, cheapestShownAmount: shownAmounts.length > 0 ? Math.min(...shownAmounts) : null };
      })(),
      builderName: project.builder.name,
      builderRoute: { name: 'builder', params: { slug: project.builder.name.toSlug(), builderId: project.builder.id } },
    };
  })
);

// La colonne « à partir de » n'apparaît à côté des colonnes de chambres que lorsqu'au moins un projet
// a un prix plus bas qu'aucune d'elles n'affiche, par exemple un studio trop rare pour avoir sa colonne
const showStartingPriceColumn = computed(() => bedroomColumns.value.length === 0 || projectRows.value.some((row) => row.priceAmount !== null && (row.cheapestShownAmount === null || row.priceAmount < row.cheapestShownAmount)));
</script>

<template>
  <section v-if="statistics" class="space-y-4">
    <SectionHeader>{{ t('views.editorial.statistics.header', { title: props.title.split(':')[0]?.trim() ?? props.title }) }}</SectionHeader>

    <p class="max-w-none text-gray-700">
      <strong>{{ summarySentence }}</strong>
      {{ ' ' }}{{ typesSentence }}
      <template v-if="bedroomsSentence">{{ ' ' }}{{ bedroomsSentence }}</template>
      <template v-if="amenitiesSentence">{{ ' ' }}{{ amenitiesSentence }}</template>
      {{ ' ' }}{{ updatedSentence }}
    </p>

    <details class="group rounded-lg border border-gray-200 bg-white p-4">
      <summary class="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-gray-900">
        {{ t('views.editorial.statistics.tables-toggle') }}
        <FontAwesomeIcon :icon="faChevronDown" aria-hidden="true" class="text-[0.75em] text-gray-400 transition-transform group-open:rotate-180" />
      </summary>

      <div class="mt-4 space-y-6">
        <div v-if="dwellingRows.length > 0" class="space-y-2">
          <h3 class="font-medium text-gray-900">{{ t('views.editorial.statistics.types-table.header') }}</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full text-left text-sm">
              <thead>
                <tr class="border-b border-gray-200 text-gray-500">
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.types-table.type') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.types-table.projects') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.types-table.bedrooms') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.types-table.price-range') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.types-table.floor-area') }}</th>
                  <th class="py-2 font-medium" scope="col">{{ t('views.editorial.statistics.types-table.price-per-square-foot') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in dwellingRows" :key="row.id" class="border-b border-gray-100 text-gray-700">
                  <td class="py-2 pr-4 font-medium text-gray-900">{{ row.name }}</td>
                  <td class="py-2 pr-4">{{ row.projectCount }}</td>
                  <td class="py-2 pr-4">{{ row.bedrooms ?? '—' }}</td>
                  <td class="py-2 pr-4">{{ row.priceRange }}</td>
                  <td class="py-2 pr-4">
                    <template v-if="row.floorArea">
                      {{ row.floorArea }}
                      <span v-if="row.floorAreaRange" class="text-gray-400">({{ row.floorAreaRange }})</span>
                    </template>
                    <template v-else>—</template>
                  </td>
                  <td class="py-2">{{ row.pricePerSquareFoot ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="lotRows.length > 0" class="space-y-2">
          <h3 class="font-medium text-gray-900">{{ t('views.editorial.statistics.lots-table.header') }}</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full text-left text-sm">
              <thead>
                <tr class="border-b border-gray-200 text-gray-500">
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.lots-table.type') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.lots-table.projects') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.lots-table.price-range') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.lots-table.floor-area') }}</th>
                  <th class="py-2 font-medium" scope="col">{{ t('views.editorial.statistics.lots-table.price-per-square-foot') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in lotRows" :key="row.id" class="border-b border-gray-100 text-gray-700">
                  <td class="py-2 pr-4 font-medium text-gray-900">{{ row.name }}</td>
                  <td class="py-2 pr-4">{{ row.projectCount }}</td>
                  <td class="py-2 pr-4">{{ row.priceRange }}</td>
                  <td class="py-2 pr-4">
                    <template v-if="row.floorArea">
                      {{ row.floorArea }}
                      <span v-if="row.floorAreaRange" class="text-gray-400">({{ row.floorAreaRange }})</span>
                    </template>
                    <template v-else>—</template>
                  </td>
                  <td class="py-2">{{ row.pricePerSquareFoot ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="projectRows.length > 0" class="space-y-2">
          <h3 class="font-medium text-gray-900">{{ t('views.editorial.statistics.projects-table.header') }}</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full text-left text-sm">
              <thead>
                <tr class="border-b border-gray-200 text-gray-500">
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.project') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.type') }}</th>
                  <!-- Une colonne de prix par nombre de chambres remplace la fourchette et le prix de départ quand le tableau reste assez rempli -->
                  <template v-if="bedroomColumns.length > 0">
                    <th v-for="column in bedroomColumns" :key="column.bedrooms" class="py-2 pr-4 font-medium" scope="col">
                      {{ bedroomColumnLabel(column.bedrooms) }}
                    </th>
                  </template>
                  <th v-else class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.bedrooms') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.bathrooms') }}</th>
                  <th class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.floor-area') }}</th>
                  <th v-if="showStartingPriceColumn" class="py-2 pr-4 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.starting-price') }}</th>
                  <th class="py-2 font-medium" scope="col">{{ t('views.editorial.statistics.projects-table.builder') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in projectRows" :key="row.id" class="border-b border-gray-100 text-gray-700">
                  <td class="py-2 pr-4">
                    <RouterLink :to="row.route" class="text-ph-green-700 hover:text-ph-green-800 font-medium">{{ row.name }}</RouterLink>
                  </td>
                  <td class="py-2 pr-4">{{ row.category ?? '—' }}</td>
                  <template v-if="bedroomColumns.length > 0">
                    <td v-for="cell in row.bedroomPrices" :key="cell.bedrooms" class="py-2 pr-4">{{ cell.price ?? '—' }}</td>
                  </template>
                  <td v-else class="py-2 pr-4">{{ row.bedrooms ?? '—' }}</td>
                  <td class="py-2 pr-4">{{ row.bathrooms ?? '—' }}</td>
                  <td class="py-2 pr-4">{{ row.floorArea ?? '—' }}</td>
                  <td v-if="showStartingPriceColumn" class="py-2 pr-4">{{ row.price ?? '—' }}</td>
                  <td class="py-2">
                    <RouterLink :to="row.builderRoute" class="hover:text-ph-green-800">{{ row.builderName }}</RouterLink>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="text-xs text-gray-500">{{ t('views.editorial.statistics.projects-table.caption', { siteName: t('global.site-name') }) }}</p>
        </div>
      </div>
    </details>

    <div v-if="faqItems.length > 0" class="space-y-3">
      <h3 class="font-medium text-gray-900">
        {{ props.locationPhrase ? t('views.editorial.statistics.faq.header', { locationPhrase: props.locationPhrase }) : t('views.editorial.statistics.faq.header-generic') }}
      </h3>
      <details v-for="item in faqItems" :key="item.question" class="group rounded-lg border border-gray-200 bg-white p-4">
        <summary class="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-gray-900">
          {{ item.question }}
          <FontAwesomeIcon :icon="faChevronDown" aria-hidden="true" class="text-[0.75em] text-gray-400 transition-transform group-open:rotate-180" />
        </summary>
        <p class="mt-3 text-gray-600">{{ item.answer }}</p>
      </details>
    </div>

    <p class="text-xs text-gray-500">
      <i18n-t keypath="views.editorial.statistics.sources" scope="global" tag="span">
        <template #gcr>
          <a class="hover:text-ph-green-800 underline" href="https://www.garantiegcr.com" rel="noopener" target="_blank">{{ t('views.editorial.statistics.sources-gcr') }}</a>
        </template>
        <template #revenuQuebec>
          <a class="hover:text-ph-green-800 underline" href="https://www.revenuquebec.ca" rel="noopener" target="_blank">{{ t('views.editorial.statistics.sources-revenu-quebec') }}</a>
        </template>
      </i18n-t>
      {{ ' ' }}{{ updatedSentence }}
    </p>
  </section>
</template>

<style lang="css" scoped></style>
